import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { staffMandateService } from "../../services/api";
import { STAFF_MANDATE_ENABLED } from "../../config/featureFlags";

// TP-Coach-Mandate (F2) — staff sibling of PendingInvitesBanner: the invitee
// respond surface for the internal lane (D-006). Flag-gated and rendered for
// Staff/Coach roles only; the player banner stays untouched.
//
// Data source: GET /users/staff-mandate-invites/pending/ (bare list, mirror of
// the player /users/player-invites/list/ shape) — rows carry
// {id, agent_name, agency_name, mandate:{contract_type, is_exclusive,
//  start_date, end_date, countries:[names]}}.
const StaffPendingInvitesBanner = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState({});
  const { user } = useSelector((state) => state.auth);

  // Only for coach/staff accounts (the mandate subjects of this lane).
  const isSubject = user?.roles?.some(
    (role) => role === "Staff" || role === "Coach"
  );

  const fetchPendingInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await staffMandateService.getPendingMandateInvitations();
      if (response.success) {
        setInvitations(response.data);
      }
    } catch (error) {
      console.error("Error fetching pending staff mandate invitations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (STAFF_MANDATE_ENABLED && isSubject) {
      fetchPendingInvitations();
    }
  }, [isSubject, fetchPendingInvitations]);

  const handleResponse = async (inviteId, action) => {
    setResponding((prev) => ({ ...prev, [inviteId]: true }));

    try {
      const response = await staffMandateService.respondToMandateInvitation(
        inviteId,
        action
      );

      if (response.success) {
        toast.success(
          action === "accept"
            ? "Representation invitation accepted successfully!"
            : "Representation invitation declined.",
          {
            duration: 4000,
            style: {
              background: action === "accept" ? "#f0fdf4" : "#fef2f2",
              border:
                action === "accept" ? "1px solid #22c55e" : "1px solid #ef4444",
              color: action === "accept" ? "#15803d" : "#dc2626",
            },
          }
        );

        // Remove the invitation from the list
        setInvitations((prev) => prev.filter((inv) => inv.id !== inviteId));
      } else {
        toast.error(response.error || `Failed to ${action} invitation`);
      }
    } catch (error) {
      console.error(`Error ${action}ing staff mandate invitation:`, error);
      toast.error(`Failed to ${action} invitation. Please try again.`);
    } finally {
      setResponding((prev) => ({ ...prev, [inviteId]: false }));
    }
  };

  if (!STAFF_MANDATE_ENABLED || !isSubject || loading || invitations.length === 0) {
    return null;
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 mt-3">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="w-5 h-5 text-blue-400 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-blue-800 mb-2">
            {invitations.length === 1
              ? "You have a pending representation invitation"
              : `You have ${invitations.length} pending representation invitations`}
          </h3>

          <div className="space-y-3">
            {invitations.map((invitation) => {
              const mandate = invitation.mandate || {};
              const agentLabel =
                invitation.agent_name ||
                invitation.agency_name ||
                invitation.sender_name ||
                "Agent";
              return (
                <div
                  key={invitation.id}
                  className="bg-white rounded-lg p-3 border border-blue-100"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 font-medium">
                        Representation offer from {agentLabel}
                        {invitation.agency_name &&
                          invitation.agent_name &&
                          invitation.agency_name !== invitation.agent_name && (
                            <span className="text-gray-500 font-normal">
                              {" "}
                              ({invitation.agency_name})
                            </span>
                          )}
                      </p>
                      <div className="mt-1 text-xs text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Type:</span>{" "}
                          {mandate.contract_type === "mandate"
                            ? "Mandate"
                            : "Representation"}{" "}
                          Contract
                        </p>
                        {mandate.contract_type === "mandate" && (
                          <>
                            <p>
                              <span className="font-medium">Countries:</span>{" "}
                              {mandate.countries?.length > 0
                                ? mandate.countries.join(", ")
                                : "All countries"}
                            </p>
                            <p>
                              <span className="font-medium">Exclusive:</span>{" "}
                              {mandate.is_exclusive ? "Yes" : "No"}
                            </p>
                          </>
                        )}
                        <p>
                          <span className="font-medium">Duration:</span>{" "}
                          {mandate.start_date} to {mandate.end_date}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleResponse(invitation.id, "decline")}
                        disabled={responding[invitation.id]}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          responding[invitation.id]
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-red-100 text-red-700 hover:bg-red-200"
                        }`}
                      >
                        {responding[invitation.id]
                          ? "Processing..."
                          : "Decline"}
                      </button>
                      <button
                        onClick={() => handleResponse(invitation.id, "accept")}
                        disabled={responding[invitation.id]}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          responding[invitation.id]
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                        }`}
                      >
                        {responding[invitation.id] ? "Processing..." : "Accept"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffPendingInvitesBanner;
